import { useCallback, useEffect } from 'react'

import type { AgentStatusParams } from '@oneToOne/agentStatus/params'
import { configs } from '@oneToOne/agentStatus/params'
import {
  trackEmbedImpression,
  trackEmbedInstance,
  trackWidgetClick,
} from '@oneToOne/agentStatus/tracking/agentStatusTracking'
import { useTarget } from '@oneToOne/shared/contexts/TargetContext'
import { useChatChannelQuery } from '@oneToOne/shared/hooks/useChatChannelQuery'
import type { LiveHelperActionProps } from '@oneToOne/shared/utils/liveHelperActions'
import { liveHelperActions } from '@oneToOne/shared/utils/liveHelperActions'
import { isOnline } from '@oneToOne/shared/utils/widgetAvailability'

import { Slot } from '@core/components/Slot'
import { resolveParams } from '@core/params'

/**
 * The Agents Status component
 */
export const AgentStatus: React.FC<{
  initialParams: AgentStatusParams
  widgetAttributes: Record<string, string>
}> = ({ initialParams, widgetAttributes }) => {
  const params = resolveParams(
    {
      defaultParams: {
        dismiss_after_conversation_ended: true,
        // The expectation is to show the widget immediately,
        // hence these params probably should never be false
        bypass_qualify_visitors: true,
        bypass_widget_availability_hidden: true,
        ...initialParams,
      },
      widgetAttributes,
    },
    configs,
    /* silent */ true,
  )

  useEffect(() => {
    trackEmbedInstance(params)
  }, [params])

  const {
    data: chatChannelConfig,
    // isPending (not v5's isLoading) so a query disabled for a missing
    // chat_channel_id keeps rendering the loading slot, as in v4.
    isPending: isLoadingChatChannelConfig,
    error,
  } = useChatChannelQuery(params.chat_channel_id, params.api_host)

  const trackSlotImpression = useCallback(() => {
    trackEmbedImpression(params, !isOnline(chatChannelConfig))
  }, [params, chatChannelConfig])

  const isAvailable = isOnline(chatChannelConfig)

  const target = useTarget()
  useEffect(() => {
    if (
      !isAvailable ||
      params.action === 'enableFeatures' ||
      params.action === 'disableFeatures'
    ) {
      return
    }

    const action = params.action || 'startCall'

    const handleClick = () => {
      const config: LiveHelperActionProps = {
        chat_channel_id: params.chat_channel_id!,
      }
      if (params.dismiss_after_conversation_ended) {
        config['dismiss_after_conversation_ended'] = true
      }
      if (params.bypass_sa_availability) {
        config['bypass_sa_availability'] = true
      }
      if (params.bypass_qualify_visitors) {
        config['bypass_qualify_visitors'] = true
      }
      if (params.bypass_widget_availability_hidden) {
        config['bypass_widget_availability_hidden'] = true
      }
      liveHelperActions[action](config)
      trackWidgetClick(params, action, !isOnline(chatChannelConfig))
    }

    target?.addEventListener('click', handleClick)

    return () => {
      target?.removeEventListener('click', handleClick)
    }
  }, [target, isAvailable, params, chatChannelConfig])

  if (error) {
    return null
  } else if (isLoadingChatChannelConfig) {
    return <slot name="loading" />
  } else if (isAvailable) {
    return <Slot name="available" impressionFn={trackSlotImpression} />
  } else {
    return <Slot name="not-available" impressionFn={trackSlotImpression} />
  }
}
